import React, { useEffect, useState } from "react";
import { FileManager } from "@cubone/react-file-manager";
import "@cubone/react-file-manager/dist/style.css";
import config, { env } from "../config";
import qs from "query-string";
import AuthService from "../auth/AuthService";
import { getNewFileName } from "../common/CommonFun";
import { message } from "antd";
import './styles.css'

const Auth = new AuthService();

const FileManagerComponent = () => {
  const [files, setFiles] = useState([]);
  const [currentPath, setCurrentPath] = useState("/");
  const [activeFolder, setActiveFolder] = useState(null)
  const [loading, setLoading] = useState(false);

  const buildFileManagerFolders = (apiFolders = []) => {
    const validFolders = apiFolders.filter(
      (f) => typeof f.name === "string" && f.name.trim() !== "" && f.type
    );

    const staticFolders = [
      {
        name: "Public",
        isDirectory: true,
        path: "/Public",
        updatedAt: new Date().toISOString(),
      },
      {
        name: "Personal",
        isDirectory: true,
        path: "/Personal",
        updatedAt: new Date().toISOString(),
      },
    ];

    // Build folder map
    const folderMap = {};
    validFolders.forEach((folder) => {
      folderMap[folder.id] = { ...folder, children: [] };
    });

    // Attach children to parent
    validFolders.forEach((folder) => {
      if (folder.parent_id && folderMap[folder.parent_id]) {
        folderMap[folder.parent_id].children.push(folderMap[folder.id]);
      }
    });

    const buildPaths = (folder, basePath) => {
      const currentPath = `${basePath}/${folder.name}`.replace(/\/+/g, "/");
      const node = {
        id: folder.id,
        name: folder.name,
        isDirectory: true,
        path: currentPath,
        parent_id: folder.parent_id,
        updatedAt: folder.updatedAt || new Date().toISOString(),
      };
      const children = folder.children || [];
      const nested = children.flatMap((child) =>
        buildPaths(child, currentPath)
      );
      return [node, ...nested];
    };

    const dynamicFolders = validFolders
      .filter((folder) => folder.parent_id === null)
      .flatMap((folder) => {
        const rootPath = folder.type === 1 ? "/Public" : "/Personal";
        return buildPaths(folderMap[folder.id], rootPath);
      });

    return [...staticFolders, ...dynamicFolders];
  };

  const buildFilesForCurrentPath = (apiFiles = [], path) => {
    return apiFiles
      .filter(file => file?.name)
      .map(file => ({
        ...file,
        name: file.name,
        isDirectory: false,
        path: `${path}/${file.name}`.replace(/\/+/g, "/"),
        updatedAt: file.updatedAt || new Date().toISOString(),
        size: file.size,
      }));
  };

  const handleCreateFolder = async (name, parent) => {
    if (!name || !/^[a-zA-Z0-9_-]+$/.test(name)) {
      message.error("Use only letters, numbers, (_) or (-) in folder name");
      return;
    }

    const path = parent?.path?.toLowerCase() || "";
    const type = path.startsWith("/personal") ? 2 : 1;

    const body = {
      name,
      type,
      parent_id: parent?.id || null,
    };

    try {
      const res = await Auth.fetch(`/file/folder`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      if (res?.ack) fetchFolders();
      return res;
    } catch (error) {
      console.error("Error creating folder:", error);
      return;
    }
  };

  const fetchFolders = async () => {
    setLoading(true);
    try {
      const res = await Auth.fetch("/file/folders");
      const folders = res?.ack ? res.folders : [];
      const structured = buildFileManagerFolders(folders);
      setFiles(structured);
      setFiles(structured);
    } catch (error) {
      console.error("Error loading folders:", error);
      setFiles(buildFileManagerFolders([]));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFolder = async (files = []) => {
    if (!Array.isArray(files) || files.length === 0) return;

    for (const file of files) {
      if (file?.isDirectory) {
        try {
          const res = await Auth.fetch(`/file/folder`, {
            method: "DELETE",
            body: JSON.stringify({ id: file.id }),
          });

          if (res?.ack) {
            fetchFolders();
          }
        } catch (error) {
          console.log("Error deleting folder:", file.name, error);
          return;
        }
      } else {
        try {
          const res = await Auth.fetch(`/file/file`, {
            method: "DELETE",
            body: JSON.stringify({ id: file.id }),
          });

          if (res?.ack) {
            getFilesByFolder(file?.folder_id);
          }
        } catch (error) {
          console.log("Error deleting folder:", file.name, error);
          return;
        }
      }
    }
  };

  const getPermissions = (path) => {
    if (path === "/") {
      return {
        create: false,
        upload: false,
        download: false,
        rename: false,
        copy: false,
        move: false,
        delete: false,
      };
    }

    if (path === "/Public" || path === "/Personal") {
      return {
        create: true,
        upload: false,
        download: false,
        rename: false,
        copy: false,
        move: false,
        delete: true,
      };
    }

    return {
      create: true,
      upload: true,
      download: false,
      rename: false,
      copy: false,
      move: false,
      delete: true,
    };
  };

  const getUploadUrl = async (file, folder) => {
    const params = {
      bucket: config.tms_uploads_bucket,
      ContentType: file?.type,
      Key: config.env + "/Documents" + folder.path + "/" + file?.name,
    };

    const response = await fetch(
      `${config.domain}/generate-put-url?${qs.stringify(params)}`,
      {
        headers: {
          "Content-Type": params.ContentType,
        },
      }
    );
    return response.json();
  };

  const uploadFileToS3 = async (uploadUrl, file, contentType) => {
    const response = await fetch(uploadUrl.putURL, {
      method: "PUT",
      body: file,
      headers: {
        "Content-Type": contentType,
      },
    });
    return response
  };

  const getFilesByFolder = async (folder_id) => {
    setLoading(true);
    try {
      const folder = files.find(f => f.id === folder_id);
      if (!folder || !folder_id) return;

      const res = await Auth.fetch(`/file/files?folder_id=${folder_id}`);
      if (res?.ack) {
        const newFiles = buildFilesForCurrentPath(res.files, folder.path);
        setFiles(prev =>
          [
            ...prev.filter(f => !(f.path?.startsWith(folder.path + "/") && !f.isDirectory)),
            ...newFiles
          ]
        );
      }
      return res;

    } catch (error) {
      console.error("Error fetching files by folder:", error);
      return;
    } finally {
      setLoading(false);
    }
  }

  const handleUploadFile = async (file, parent) => {
    const fileName = getNewFileName(file.name);
    const renamedFile = new File([file], fileName, {
      type: file.type,
      lastModified: file.lastModified,
    });

    try {
      const uploadUrl = await getUploadUrl(renamedFile, parent);
      const res = await uploadFileToS3(uploadUrl, renamedFile, renamedFile.type);

      const body = {
        name: fileName,
        file_type: file.type,
        folder_id: parent?.id || null,
        size: file.size
      }

      if (res?.ok) {
        const res = await Auth.fetch(`/file/file`, {
          method: "POST",
          body: JSON.stringify(body),
        });

        if (res?.ack) {
          getFilesByFolder(parent?.id);
        }
      }

    } catch (error) {
      console.log("Error uploading file:", error);
    }
  };

  useEffect(() => {
    fetchFolders();
  }, []);

  return (
    <div className="file-manager-container">
      <h2 className="page-title">Documents</h2>
      <FileManager
        files={files}
        height="calc(100vh - 210px)"
        fontFamily="Arial, sans-serif"
        primaryColor="#1677ff"
        initialPath={currentPath}
        fileUploadConfig={{
          url: `${config.domain}/generate-put-url`,
          method: "GET",
        }}
        isLoading={loading}
        onFileOpen={(folder) => {
          setCurrentPath(folder?.path);
          setActiveFolder(folder?.id)
          getFilesByFolder(folder?.id, folder.path)
        }}
        onCreateFolder={handleCreateFolder}
        onDelete={handleDeleteFolder}
        onFileUploading={handleUploadFile}
        permissions={getPermissions(currentPath)}
        onRefresh={async () => {
          await fetchFolders()
          await getFilesByFolder(activeFolder)
        }}
        filePreviewPath={`https://d35jitumpuemmk.cloudfront.net/${env}/Documents`}
      />
    </div>
  );
};

export default FileManagerComponent;
